import React from 'react';
import { useContext, useState, useRef } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { Divider, Box, Fab, Button, IconButton, Popover, Typography } from '@mui/material';
import { DynamicsChart } from '/@/app/components/DynamicsChart';
import { AppContext } from '/@/app/AppContext';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import TocIcon from '@mui/icons-material/Toc';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { useApi} from '/@/app/Api';
import { MeasurementState } from '/@/app/views/FreeMeasurementsView/MeasurementState';

import { AddChangeMeasurement } from '/@/app/views/FreeMeasurementsView/AddChangeMeasurement';
import { DeleteMeasurement } from '/@/app/views/FreeMeasurementsView/DeleteMeasurement';
import { AddChangeParameter } from "/@/app/views/FreeMeasurementsView/AddChangeParameter";
import { ChangeSections } from "/@/app/views/FreeMeasurementsView/ChangeSections";
import { DeleteParameter } from "/@/app/views/FreeMeasurementsView/DeleteParameter";


import { actionDialogState } from "/@/app/views/FreeMeasurementsView/ActionDialog";


const MeasurementActions = observer(({ measurementId, parameter, onActionSelected }) => {
    const measurement = parameter.measurements.find(m => m.id === measurementId);
    if (!measurement) {
        return null;
    }

    const onChange = () => {
        onActionSelected();
        actionDialogState.setComponent(AddChangeMeasurement, measurement);
    }
    const onDelete = () => {
        onActionSelected();
        actionDialogState.setComponent(DeleteMeasurement, measurement);
    }

    return (
        <Box sx={{ pt: 1, display: "flex", flexFlow: "row wrap", gap: 2, justifyContent: "center" }}>
            <Button onClick={onChange} size="small" color="primary" variant="outlined">Изменить</Button>
            <Button onClick={onDelete} size="small" color="error" variant="outlined">Удалить</Button>
        </Box>
    );
});


const ParameterActions = observer(({ parameter, onActionSelected }) => {
    const api=useApi();
    return (
        <Box sx={{ p: 1, display: "flex", flexFlow: "column nowrap", alignItems: "left", gap: 1 }}>
            <IconButton
                onClick={() => {
                    onActionSelected();
                    actionDialogState.setComponent(
                        AddChangeMeasurement,
                        new MeasurementState(null, parameter, api)
                    );
                }}
                size="small" color="success" variant="outlined"
            >
                <AddIcon/> Добавить измерение
            </IconButton>
            <Divider />
            <IconButton
                onClick={() => {
                    onActionSelected();
                    actionDialogState.setComponent(AddChangeParameter, parameter);
                }}
                size="small" color="warning" variant="outlined"
            >
                <EditIcon /> Изменить параметр
            </IconButton>
            <IconButton
                onClick={() => {
                    onActionSelected();
                    actionDialogState.setComponent(ChangeSections, parameter);
                }}
                size="small" color="warning" variant="outlined"
            >
                <TocIcon /> Выбрать разделы
            </IconButton>
            <IconButton
                onClick={() => {
                    onActionSelected();
                    actionDialogState.setComponent(DeleteParameter, parameter);
                }}
                size="small" color="error" variant="outlined"
                disabled={parameter.panel_bound}
            >
                <DeleteForeverIcon /> Удалить параметр
            </IconButton>
            {parameter.panel_bound &&
                <Typography variant="caption" sx={{ color: "lightgrey" }}>параметры из панели нельзя удалять</Typography>
            }
        </Box>
    );
});


export const ParameterView = observer(({ state }) => {
    const api=useApi();
    const { panel } = useContext(AppContext).bootstrapData;

    const [parameterActionsOpened, setParameterActionsOpened] = useState(false);
    const parameterActionsAnchor = useRef(null);

    // closures here
    const BoundMeasurementActions = ({ measurementId, closeDialog }) => {
        return <MeasurementActions
            measurementId={measurementId}
            parameter={state}
            onActionSelected={() => {
                closeDialog();
            }}
        />;
    };
    const AddMeasurementButton = ({}) => {
        const add = () => {
            actionDialogState.setComponent(
                AddChangeMeasurement,
                new MeasurementState(null, state, api)
            );
        };
        return (
            <Fab
                sx={{ backgroundColor: "#5bb15c", color: "white" }}
                onClick={add}
                size="small"
            >
                <AddIcon />
            </Fab>
        );
    };

    // Make normal array instead of MobX proxy object.
    const diagramDataCopy = toJS(state.diagramData);

    return (
        <Box sx={{ mt: 1, mb: 1 }}>
            <Box>
                <IconButton
                    size="small"
                    sx={{ float: "right" }}
                    ref={parameterActionsAnchor}
                    onClick={() => setParameterActionsOpened(true)}
                >
                    <MoreHorizIcon />
                </IconButton>
                <Typography sx={{ pl: 2, pr: 2}}>{state.name}{state.unit && `, ${state.unit}`}</Typography>
            </Box>

            <Popover
                open={parameterActionsOpened}
                anchorEl={parameterActionsAnchor.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                onClose={() => setParameterActionsOpened(false)}
            >
                <ParameterActions
                    parameter={state}
                    onActionSelected={() => setParameterActionsOpened(false)}
                />
            </Popover>

            <DynamicsChart
                data={diagramDataCopy}
                CustomPopperComponent={BoundMeasurementActions}
                CustomNoDataComponent={AddMeasurementButton}
                currentPanel={panel}
            />
        </Box>
    );
});
